import { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { BRAND_SHAREABLE_COLUMNS, useSetBrandColumns } from "../../hooks/usePlanReview";
import { errorMessage, primaryButtonSx } from "../approvals/approvalUi";
import { ALL_COLUMNS } from "./InfluencerTable";

// Team picks which columns the plan's brand link shows. Saved on the plan and
// applied to the live link right away. The server enforces the same list, so
// this is a convenience, not the guard.
const LABELS = Object.fromEntries(ALL_COLUMNS.map((c) => [c.key, c.label]));

const BrandColumnsDialog = ({ open, onClose, planId, columns }) => {
  const { enqueueSnackbar } = useSnackbar();
  const setColumns = useSetBrandColumns(planId);
  const [selected, setSelected] = useState(() => new Set(columns));

  const toggle = (key) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const save = () =>
    setColumns
      // Catalog order, so the brand sees columns in the same order as the Plans table.
      .mutateAsync(BRAND_SHAREABLE_COLUMNS.filter((k) => selected.has(k)))
      .then(() => {
        enqueueSnackbar("Brand view updated", { variant: "success" });
        onClose();
      })
      .catch((e) => enqueueSnackbar(errorMessage(e), { variant: "error" }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Columns the brand sees</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "#6B7280", mb: 1.5 }}>
          Creator name, handle, profile link and approval status are always shown. Influencer cost, CPV/CPE, last
          costs, POC and contact details are never shared.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
          <Button size="small" onClick={() => setSelected(new Set(BRAND_SHAREABLE_COLUMNS))} sx={{ textTransform: "none" }}>Select all</Button>
          <Button size="small" onClick={() => setSelected(new Set())} sx={{ textTransform: "none" }}>Clear</Button>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
          {BRAND_SHAREABLE_COLUMNS.map((key) => (
            <FormControlLabel
              key={key}
              control={<Checkbox size="small" checked={selected.has(key)} onChange={() => toggle(key)} />}
              label={<Typography sx={{ fontSize: 14 }}>{LABELS[key] || key}</Typography>}
            />
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" disabled={setColumns.isPending} onClick={save} sx={primaryButtonSx}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrandColumnsDialog;
